import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import Tooltip from '../../components/shared/Tooltip';

interface DmsRecord {
  id: string;
  vin: string | null;
  buyerFirstName: string | null;
  buyerLastName: string | null;
  outcome: string | null;
  saleDate: string | null;
  repExternalId: string | null;
  source: string | null;
  createdAt: string;
}

const OUTCOME_STYLES: Record<string, string> = {
  CLOSED:           'bg-secondary/10 text-secondary border-secondary/20',
  LOST:             'bg-error/10 text-error border-error/20',
  APPOINTMENT_SET:  'bg-primary/10 text-primary border-primary/20',
  PENDING:          'bg-on-surface/5 text-on-surface/40 border-outline-variant/20',
};

const OUTCOMES = ['PENDING', 'APPOINTMENT_SET', 'CLOSED', 'LOST'];

const RecordForm: React.FC<{
  vin: string; setVin: (v: string) => void;
  firstName: string; setFirstName: (v: string) => void;
  lastName: string; setLastName: (v: string) => void;
  outcome: string; setOutcome: (v: string) => void;
  saleDate: string; setSaleDate: (v: string) => void;
  repExternalId: string; setRepExternalId: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitting: boolean;
  submitLabel: string;
  requireVin?: boolean;
}> = ({ vin, setVin, firstName, setFirstName, lastName, setLastName, outcome, setOutcome, saleDate, setSaleDate, repExternalId, setRepExternalId, onSubmit, onCancel, submitting, submitLabel, requireVin }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">
        VIN {requireVin && <span className="text-error">*</span>}
      </label>
      <input
        value={vin}
        onChange={(e) => setVin(e.target.value)}
        placeholder="e.g. 1HGCM82633A123456"
        className="w-full bg-transparent border-b border-outline-variant/40 focus:border-primary pb-2 text-on-surface text-sm font-mono focus:outline-none transition-colors placeholder:text-on-surface/20"
      />
    </div>
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Rep External ID</label>
      <input
        value={repExternalId}
        onChange={(e) => setRepExternalId(e.target.value)}
        placeholder="Matches User.externalId"
        className="w-full bg-transparent border-b border-outline-variant/40 focus:border-primary pb-2 text-on-surface text-sm focus:outline-none transition-colors placeholder:text-on-surface/20"
      />
    </div>
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Buyer First Name</label>
      <input
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        placeholder="e.g. John"
        className="w-full bg-transparent border-b border-outline-variant/40 focus:border-primary pb-2 text-on-surface text-sm focus:outline-none transition-colors placeholder:text-on-surface/20"
      />
    </div>
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Buyer Last Name</label>
      <input
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        placeholder="e.g. Smith"
        className="w-full bg-transparent border-b border-outline-variant/40 focus:border-primary pb-2 text-on-surface text-sm focus:outline-none transition-colors placeholder:text-on-surface/20"
      />
    </div>
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Outcome</label>
      <div className="relative">
        <select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          className="w-full bg-transparent border-b border-outline-variant/40 focus:border-primary pb-2 text-on-surface text-sm focus:outline-none transition-colors appearance-none cursor-pointer pr-6"
        >
          {OUTCOMES.map((o) => (
            <option key={o} value={o} className="bg-white dark:bg-slate-900">{o}</option>
          ))}
        </select>
        <span className="material-symbols-outlined absolute right-0 top-0 text-[16px] text-on-surface/30 pointer-events-none">expand_more</span>
      </div>
    </div>
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Sale Date</label>
      <input
        type="date"
        value={saleDate}
        onChange={(e) => setSaleDate(e.target.value)}
        className="w-full bg-transparent border-b border-outline-variant/40 focus:border-primary pb-2 text-on-surface text-sm focus:outline-none transition-colors"
      />
    </div>
    <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
      <button
        onClick={onCancel}
        className="h-10 px-6 rounded-xl font-bold text-xs uppercase tracking-widest text-on-surface/50 hover:text-on-surface transition-colors"
      >
        Cancel
      </button>
      <button
        onClick={onSubmit}
        disabled={submitting || (requireVin ? !vin.trim() : false)}
        className="h-10 px-10 rounded-xl font-bold bg-primary hover:bg-primary/90 text-on-primary shadow-md shadow-primary/10 transition-all active:scale-95 text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting ? 'Saving...' : submitLabel}
      </button>
    </div>
  </div>
);

const DmsSettingsPanel: React.FC = () => {
  const [records, setRecords] = useState<DmsRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [addVin, setAddVin] = useState('');
  const [addFirstName, setAddFirstName] = useState('');
  const [addLastName, setAddLastName] = useState('');
  const [addOutcome, setAddOutcome] = useState('PENDING');
  const [addSaleDate, setAddSaleDate] = useState('');
  const [addRepId, setAddRepId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editVin, setEditVin] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editOutcome, setEditOutcome] = useState('PENDING');
  const [editSaleDate, setEditSaleDate] = useState('');
  const [editRepId, setEditRepId] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/dms/records?limit=25');
      setRecords(res.data.data ?? []);
      setTotal(res.data.meta?.total ?? 0);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!addVin.trim()) { toast.error('VIN is required.'); return; }
    setSubmitting(true);
    try {
      await api.post('/dms/records', {
        vin: addVin.trim(),
        buyerFirstName: addFirstName.trim() || undefined,
        buyerLastName: addLastName.trim() || undefined,
        outcome: addOutcome,
        saleDate: addSaleDate || undefined,
        repExternalId: addRepId.trim() || undefined,
        source: 'manual',
      });
      toast.success('DMS record added. Correlation will run shortly.');
      setAddVin(''); setAddFirstName(''); setAddLastName('');
      setAddSaleDate(''); setAddRepId(''); setShowAdd(false);
      await load();
    } catch {
      toast.error('Failed to add record.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (r: DmsRecord) => {
    setConfirmDeleteId(null);
    setEditingId(r.id);
    setEditVin(r.vin ?? '');
    setEditFirstName(r.buyerFirstName ?? '');
    setEditLastName(r.buyerLastName ?? '');
    setEditOutcome(r.outcome ?? 'PENDING');
    setEditSaleDate(r.saleDate ? r.saleDate.slice(0, 10) : '');
    setEditRepId(r.repExternalId ?? '');
  };

  const handleSave = async (id: string) => {
    setSaving(true);
    try {
      await api.patch(`/dms/records/${id}`, {
        vin: editVin.trim() || null,
        buyerFirstName: editFirstName.trim() || null,
        buyerLastName: editLastName.trim() || null,
        outcome: editOutcome,
        saleDate: editSaleDate || null,
        repExternalId: editRepId.trim() || null,
      });
      toast.success('Record updated.');
      setEditingId(null);
      await load();
    } catch {
      toast.error('Failed to update record.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      await api.delete(`/dms/records/${id}`);
      toast.success('Record deleted.');
      setConfirmDeleteId(null);
      await load();
    } catch {
      toast.error('Failed to delete record.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-10 shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-500 group col-span-full">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary shadow-inner group-hover:scale-110 transition-transform duration-500">
            <span className="material-symbols-outlined text-[32px]">receipt_long</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">DMS Records</h2>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 mt-1.5">Outcome correlation data</p>
          </div>
        </div>
        <button
          onClick={() => { setShowAdd((v) => !v); setEditingId(null); }}
          className="inline-flex shrink-0 items-center gap-2 h-11 px-8 rounded-xl font-bold bg-primary hover:bg-primary/90 text-on-primary shadow-md shadow-primary/10 hover:shadow-xl hover:shadow-primary/20 transition-all active:scale-95 text-xs uppercase tracking-widest"
        >
          <span className="material-symbols-outlined text-[18px]">{showAdd ? 'close' : 'add_circle'}</span>
          {showAdd ? 'Cancel' : 'Add Record'}
        </button>
      </div>

      {/* Add Record Form */}
      {showAdd && (
        <div className="mb-8 p-8 bg-primary/[0.03] dark:bg-primary/[0.06] border border-primary/10 rounded-2xl space-y-6 animate-in slide-in-from-top-2 duration-300">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">New Record</p>
          <RecordForm
            vin={addVin} setVin={setAddVin}
            firstName={addFirstName} setFirstName={setAddFirstName}
            lastName={addLastName} setLastName={setAddLastName}
            outcome={addOutcome} setOutcome={setAddOutcome}
            saleDate={addSaleDate} setSaleDate={setAddSaleDate}
            repExternalId={addRepId} setRepExternalId={setAddRepId}
            onSubmit={handleAdd}
            onCancel={() => setShowAdd(false)}
            submitting={submitting}
            submitLabel="Save Record"
            requireVin
          />
        </div>
      )}

      {/* Records List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
          <span className="text-[9px] font-black tracking-widest uppercase text-on-surface/20">Loading records...</span>
        </div>
      ) : records.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 border border-dashed border-outline-variant/30 rounded-2xl gap-3">
          <span className="material-symbols-outlined text-3xl text-on-surface/10">receipt_long</span>
          <p className="text-[10px] font-black uppercase tracking-widest text-on-surface/20">No DMS records yet</p>
          <p className="text-xs text-on-surface/30 font-medium">Add one manually or configure webhook ingestion.</p>
        </div>
      ) : (
        <div className="space-y-0">
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_48px] gap-4 px-4 pb-3 border-b border-outline-variant/10">
            {['VIN', 'Buyer', 'Outcome', 'Sale Date', 'Rep', 'Source', ''].map((h, i) => (
              <span key={i} className="text-[9px] font-black uppercase tracking-widest text-on-surface/30">{h}</span>
            ))}
          </div>

          <div>
            {records.map((r) => (
              <React.Fragment key={r.id}>
                {/* Display row */}
                <div className={`grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_48px] gap-4 px-4 py-4 transition-colors items-center group/row border-b border-outline-variant/5 ${editingId === r.id ? 'bg-primary/[0.02] dark:bg-primary/[0.04]' : 'hover:bg-primary/[0.01]'}`}>
                  <span className="font-mono text-[11px] text-on-surface/60 truncate">{r.vin ?? '—'}</span>
                  <span className="text-sm font-medium text-on-surface truncate">
                    {[r.buyerFirstName, r.buyerLastName].filter(Boolean).join(' ') || '—'}
                  </span>
                  <span>
                    {r.outcome ? (
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ring-1 ring-inset ${OUTCOME_STYLES[r.outcome] ?? OUTCOME_STYLES['PENDING']}`}>
                        {r.outcome.replace('_', ' ')}
                      </span>
                    ) : (
                      <span className="text-on-surface/20 text-xs">—</span>
                    )}
                  </span>
                  <span className="text-[11px] text-on-surface/50 font-medium">
                    {r.saleDate ? new Date(r.saleDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </span>
                  <span className="font-mono text-[11px] text-on-surface/40 truncate">{r.repExternalId ?? '—'}</span>
                  <span className="text-[9px] font-black uppercase tracking-widest text-on-surface/30">{r.source ?? '—'}</span>

                  {/* Actions */}
                  <div className="flex items-center gap-1 justify-end">
                    {confirmDeleteId === r.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(r.id)}
                          disabled={deleting}
                          className="text-[9px] font-black uppercase tracking-widest text-error hover:text-error/80 transition-colors disabled:opacity-40"
                        >
                          {deleting ? '...' : 'Yes'}
                        </button>
                        <span className="text-on-surface/20 text-[9px]">/</span>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-[9px] font-black uppercase tracking-widest text-on-surface/40 hover:text-on-surface transition-colors"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
                        <Tooltip content={editingId === r.id ? 'Cancel edit' : 'Edit'}>
                          <button
                            onClick={() => editingId === r.id ? setEditingId(null) : startEdit(r)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-primary/10 text-on-surface/40 hover:text-primary transition-colors"
                          >
                            <span className="material-symbols-outlined text-[15px]">{editingId === r.id ? 'close' : 'edit'}</span>
                          </button>
                        </Tooltip>
                        <Tooltip content="Delete">
                          <button
                            onClick={() => { setEditingId(null); setConfirmDeleteId(r.id); }}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-error/10 text-on-surface/40 hover:text-error transition-colors"
                          >
                            <span className="material-symbols-outlined text-[15px]">delete</span>
                          </button>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>

                {/* Inline edit form */}
                {editingId === r.id && (
                  <div className="px-4 py-6 border-b border-outline-variant/10 bg-primary/[0.02] dark:bg-primary/[0.04] animate-in slide-in-from-top-1 duration-200">
                    <p className="text-[9px] font-black uppercase tracking-widest text-on-surface/30 mb-5">Edit Record</p>
                    <RecordForm
                      vin={editVin} setVin={setEditVin}
                      firstName={editFirstName} setFirstName={setEditFirstName}
                      lastName={editLastName} setLastName={setEditLastName}
                      outcome={editOutcome} setOutcome={setEditOutcome}
                      saleDate={editSaleDate} setSaleDate={setEditSaleDate}
                      repExternalId={editRepId} setRepExternalId={setEditRepId}
                      onSubmit={() => handleSave(r.id)}
                      onCancel={() => setEditingId(null)}
                      submitting={saving}
                      submitLabel="Update Record"
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <p className="text-[10px] font-black tracking-widest uppercase text-on-surface/20 pt-4 pl-4">
            Showing {records.length} of {total} records
          </p>
        </div>
      )}
    </div>
  );
};

export default DmsSettingsPanel;
